/**
 * Authentication
 * -------------------------------------------------------------
 * Sign-in is Microsoft Entra ID, through Supabase's Azure provider.
 *
 * Nobody registers and nobody is invited. A colleague opens the site, clicks
 * one button, Microsoft authenticates them against the Wovenworks Global
 * tenant, and Supabase creates their `auth.users` row the first time they
 * arrive. The address the app receives is one Microsoft has verified, which is
 * what lets the Miro hand-off treat `platform email = Miro email` as a fact
 * rather than a convention.
 *
 * No password ever reaches this application, so there is nothing here to
 * store, rotate or reset.
 *
 * Two services live behind one interface:
 *
 *   SupabaseAuthService   VITE_DATA_SOURCE=supabase — the real thing
 *   MockAuthService       VITE_DATA_SOURCE=mock     — signs in instantly,
 *                                                     offline, for local work
 *
 * IMPORTANT: this is a front door, not a lock. Until every image sits behind
 * Supabase row level security, anything the browser can reach is reachable
 * without signing in. See README section 13.
 */

import type { Session, SupabaseClient, User } from '@supabase/supabase-js'
import { getSupabase } from './supabaseClient'

export type UserRole = 'designer' | 'admin'

export interface AuthUser {
  id: string
  email: string
  displayName: string
  role: UserRole
}

export interface AuthSession {
  user: AuthUser
  /**
   * ISO timestamp of the access token's expiry — about an hour out.
   *
   * It is not a sign-out deadline. `offline_access` gives Supabase a refresh
   * token and supabase-js renews the access token in the background, so the
   * session survives until somebody signs out explicitly or an administrator
   * revokes it. Nothing in the app compares this value against the clock; it
   * is here to be read, not enforced.
   */
  expiresAt: string
}

export type AuthChangeEvent = 'INITIAL_SESSION' | 'SIGNED_IN' | 'SIGNED_OUT'

export type AuthChangeCallback = (event: AuthChangeEvent, session: AuthSession | null) => void

export interface Subscription {
  unsubscribe(): void
}

export interface AuthService {
  getSession(): Promise<AuthSession | null>
  onAuthStateChange(callback: AuthChangeCallback): Subscription
  /**
   * Hands the browser to Microsoft. On success this call never returns — the
   * page has already navigated away — so callers should not clear their busy
   * state afterwards. It throws only if the redirect could not be started.
   */
  signInWithMicrosoft(): Promise<void>
  signOut(): Promise<void>
}

/* -------------------------------------------------------------------------
   Who is an administrator
   -------------------------------------------------------------------------
   Everyone in the tenant can sign in; `admin` is the extra bit that opens
   Image Tag and the other library-editing tools.

   This list is public — it ships in the bundle, like any VITE_ value. That is
   fine for deciding what to draw, and useless as protection: the real limit is
   the row level security policy on the write path, which trusts the signed
   JWT and not the interface. Keep the two in step.
   ------------------------------------------------------------------------- */

const DEFAULT_ADMINS = ['hhe@wovenworksglobal.com']

const ADMIN_EMAILS = new Set(
  (import.meta.env.VITE_ADMIN_EMAILS || DEFAULT_ADMINS.join(','))
    .split(',')
    .map((entry: string) => entry.trim().toLowerCase())
    .filter(Boolean),
)

function roleFor(email: string): UserRole {
  return ADMIN_EMAILS.has(email) ? 'admin' : 'designer'
}

/**
 * Whether an address is on the administrator list.
 *
 * Exported for the Miro panel, which has no sign-in of its own: it knows who
 * it belongs to only through the address recorded when the person linked their
 * Miro account, so it asks about an email rather than reading a session.
 */
export function isAdminEmail(email: string | null | undefined): boolean {
  return Boolean(email) && ADMIN_EMAILS.has(normaliseEmail(email as string))
}

/* ------------------------------------------------------------------------- */

function normaliseEmail(email: string): string {
  return email.trim().toLowerCase()
}

export function isValidEmailShape(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())
}

/**
 * The best name Entra gave us.
 *
 * `full_name` is what Supabase normally fills in; the others are there because
 * which claims a tenant returns depends on how its directory is filled in, and
 * an empty greeting looks worse than a plain address.
 */
function displayNameFor(user: User, email: string): string {
  const meta = (user.user_metadata ?? {}) as Record<string, unknown>
  for (const key of ['full_name', 'name', 'preferred_username']) {
    const value = meta[key]
    if (typeof value === 'string' && value.trim()) return value.trim()
  }
  return email
}

function toAuthSession(session: Session | null): AuthSession | null {
  if (!session?.user) return null
  const email = normaliseEmail(session.user.email ?? '')
  return {
    user: {
      id: session.user.id,
      email,
      displayName: displayNameFor(session.user, email),
      role: roleFor(email),
    },
    expiresAt: new Date((session.expires_at ?? 0) * 1000).toISOString(),
  }
}

/* -------------------------------------------------------------------------
   The real service
   ------------------------------------------------------------------------- */

export class SupabaseAuthService implements AuthService {
  constructor(private readonly supabase: SupabaseClient) {}

  async getSession(): Promise<AuthSession | null> {
    const { data } = await this.supabase.auth.getSession()
    return toAuthSession(data.session)
  }

  onAuthStateChange(callback: AuthChangeCallback): Subscription {
    const { data } = this.supabase.auth.onAuthStateChange((event, session) => {
      // supabase-js fires more events than the app cares about — token
      // refreshes and profile updates among them. They all carry the current
      // session, so collapse everything that is not a sign-out into the two
      // events the rest of the app knows, and let the session decide.
      const mapped: AuthChangeEvent =
        event === 'SIGNED_OUT' || !session
          ? 'SIGNED_OUT'
          : event === 'INITIAL_SESSION'
            ? 'INITIAL_SESSION'
            : 'SIGNED_IN'
      callback(mapped, toAuthSession(session))
    })
    return { unsubscribe: () => data.subscription.unsubscribe() }
  }

  async signInWithMicrosoft(): Promise<void> {
    const { error } = await this.supabase.auth.signInWithOAuth({
      provider: 'azure',
      options: {
        // `email` carries the verified company address, which is the join key
        // for everything; `offline_access` is what keeps people signed in
        // between visits instead of bouncing them to Microsoft every hour.
        scopes: 'openid email profile offline_access',
        redirectTo: `${window.location.origin}/auth/callback`,
      },
    })
    if (error) throw new Error(error.message)
  }

  async signOut(): Promise<void> {
    const { error } = await this.supabase.auth.signOut()
    if (error) throw new Error(error.message)
  }
}

/* -------------------------------------------------------------------------
   The offline stand-in
   -------------------------------------------------------------------------
   Used when VITE_DATA_SOURCE is not `supabase`, so the interface can be worked
   on with no network, no tenant and no database. It signs the first known user
   straight in — there is nothing to authenticate against.
   ------------------------------------------------------------------------- */

const MOCK_USER = {
  email: 'hhe@wovenworksglobal.com',
  displayName: 'Huanrui He',
}

const SESSION_DAYS = 30
const STORAGE_KEY = 'wonderworks.auth.session'

function readStoredSession(): AuthSession | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as AuthSession
    if (!parsed?.user?.email || !parsed.expiresAt) return null
    if (new Date(parsed.expiresAt).getTime() < Date.now()) {
      window.localStorage.removeItem(STORAGE_KEY)
      return null
    }
    return parsed
  } catch {
    // Private windows and blocked site data both land here. Not signed in.
    return null
  }
}

function writeStoredSession(session: AuthSession | null): void {
  try {
    if (session) window.localStorage.setItem(STORAGE_KEY, JSON.stringify(session))
    else window.localStorage.removeItem(STORAGE_KEY)
  } catch {
    // Storage is a convenience; the session still works for this page load.
  }
}

export class MockAuthService implements AuthService {
  private listeners = new Set<AuthChangeCallback>()

  async getSession(): Promise<AuthSession | null> {
    return readStoredSession()
  }

  onAuthStateChange(callback: AuthChangeCallback): Subscription {
    this.listeners.add(callback)
    // Supabase fires an initial event asynchronously; match that.
    void Promise.resolve().then(() => {
      if (this.listeners.has(callback)) callback('INITIAL_SESSION', readStoredSession())
    })
    return {
      unsubscribe: () => {
        this.listeners.delete(callback)
      },
    }
  }

  private emit(event: AuthChangeEvent, session: AuthSession | null): void {
    for (const listener of this.listeners) listener(event, session)
  }

  async signInWithMicrosoft(): Promise<void> {
    const email = normaliseEmail(MOCK_USER.email)
    const session: AuthSession = {
      user: {
        id: `mock-${email}`,
        email,
        displayName: MOCK_USER.displayName,
        role: roleFor(email),
      },
      expiresAt: new Date(Date.now() + SESSION_DAYS * 24 * 60 * 60 * 1000).toISOString(),
    }
    // A beat, so the button's busy state is visible rather than a flicker.
    await new Promise((resolve) => setTimeout(resolve, 300))
    writeStoredSession(session)
    this.emit('SIGNED_IN', session)
  }

  async signOut(): Promise<void> {
    writeStoredSession(null)
    this.emit('SIGNED_OUT', null)
  }
}

let service: AuthService | null = null

/**
 * The active auth service.
 * VITE_DATA_SOURCE=supabase -> SupabaseAuthService (Microsoft sign-in)
 * anything else             -> MockAuthService
 *
 * A missing or half-filled Supabase configuration falls back to the mock
 * rather than throwing, so a colleague who has cloned the repository without a
 * `.env.local` still gets a working interface instead of a blank page.
 */
export function getAuthService(): AuthService {
  if (service) return service
  const supabase = import.meta.env.VITE_DATA_SOURCE === 'supabase' ? getSupabase() : null
  service = supabase ? new SupabaseAuthService(supabase) : new MockAuthService()
  return service
}

/** True when sign-in goes to Microsoft rather than the offline stand-in. */
export function isRealSignIn(): boolean {
  return getAuthService() instanceof SupabaseAuthService
}
